import { Button, Divider, Space, Table, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useContext, useEffect, useState } from "react";
import { ProjectProps, TagProps } from "../../interfaces/interfaces";
import Link from "antd/es/typography/Link";
import { TablePaginationConfig } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";
import DataContext from "../../context/DataContext";
import { ACTIVITY_INITIAL_STATE, TASK_INITIAL_STATE } from "../../utils/data";
import {
  ClockCircleOutlined,
  FormOutlined,
  CopyOutlined,
  EditOutlined,
  StarOutlined,
  RetweetOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

export const ProjectList = ({
  onChangePage,
  onSelectOne,
  onSelectCustomer,
  onDeleteProject,
  rowSelection,
  hideColumns,
  showDetails = true,
  active = true,
}: {
  onChangePage: (current: number, pageSize: number) => void;
  onSelectOne: (project: ProjectProps) => void;
  onSelectCustomer?: (customer: string) => void;
  onDeleteProject?: (
    project: ProjectProps,
    pageSize: number,
    currentPage: number,
  ) => void;
  rowSelection?: any;
  hideColumns?: boolean;
  showDetails?: boolean;
  active?: boolean;
}) => {
  const [tableParams, setTableParams] = useState<TableParams>({});
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { dataLoading, projectListPaginated, openItemModal } =
    useContext(DataContext);

  useEffect(() => {
    onChangePage(currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    setPageSize(hideColumns ? 5 : 25);
  }, [hideColumns]);

  useEffect(() => {
    setTableParams({
      pagination: {
        pageSize: pageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        showTotal: (total) => `Total ${total} Projects`,
        position: ["bottomCenter"],
        total: parseInt(projectListPaginated.pagination?.total) || 0,
        onChange: (page, pageSize) => {
          setCurrentPage(page);
          setTableParams((prev) => {
            return {
              ...prev,
              pagination: {
                ...prev.pagination,
                pageSize: pageSize,
              },
            };
          });
          setPageSize(pageSize);
        },
      },
    });
  }, [projectListPaginated.pagination, hideColumns]);

  const columns: ColumnsType<ProjectProps> = [
    {
      title: "Actions",
      key: "edit",
      render: (params) => (
        <Space direction="horizontal">
          {active && (
            <Space direction="vertical" style={{ marginRight: "5px" }}>
              <Tooltip title="Add New Task for all Customers" placement="left">
                <Button
                  disabled={!params.active}
                  style={{ color: "gray", borderColor: "gray" }}
                  onClick={() => {
                    openItemModal("task", true, {
                      ...TASK_INITIAL_STATE,
                      project: params._id,
                      customers: params.customers || null,
                      origin: "project",
                    });
                  }}
                  icon={<ClockCircleOutlined />}
                  size="small"
                ></Button>
              </Tooltip>
              <Tooltip
                title="Add New Activity for all Customers"
                placement="left"
              >
                <Button
                  disabled={!params.active}
                  style={{ color: "gray", borderColor: "gray" }}
                  onClick={() => {
                    openItemModal("activity", true, {
                      ...ACTIVITY_INITIAL_STATE,
                      project: params._id,
                      customers: params.customers || null,
                      origin: "project",
                    });
                  }}
                  icon={<FormOutlined />}
                  size="small"
                ></Button>
              </Tooltip>
            </Space>
          )}
          <Space direction="vertical">
            <Tooltip title="Edit Project" placement="right">
              <Button
                style={{ color: "gray", borderColor: "gray" }}
                onClick={() => {
                  openItemModal("project", false, params);
                }}
                icon={<EditOutlined />}
                size="small"
              ></Button>
            </Tooltip>

            <>
              {active && (
                <Tooltip title="Duplicate Project" placement="right">
                  <Button
                    disabled={!params.active}
                    style={{ color: "gray", borderColor: "gray" }}
                    onClick={() => {
                      openItemModal("project", true, {
                        ...params,
                        _id: "",
                        name: params.name + " (Copy)",
                      });
                    }}
                    icon={<CopyOutlined />}
                    size="small"
                  ></Button>
                </Tooltip>
              )}
            </>
          </Space>
          <Space direction="vertical">
            {onDeleteProject && (
              <Tooltip title="Delete" placement="right">
                <Button
                  style={{ color: "gray", borderColor: "gray" }}
                  onClick={() => onDeleteProject(params, pageSize, currentPage)}
                  icon={<DeleteOutlined />}
                  size="small"
                ></Button>
              </Tooltip>
            )}
          </Space>
        </Space>
      ),
      responsive: ["xl", "lg", "md"],
      width: "10%",
    },
    {
      title: "Project Name",
      dataIndex: "name",
      key: "name",
      className: "table-row",
      render: (_, { name, customers, recurring }) => (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            maxWidth: 180,
          }}
        >
          {showDetails ? (
            <>
              <Tooltip title="Click to see Project Info">
                <Link>{name}</Link>
              </Tooltip>

              {customers.length > 0 ? (
                <></>
              ) : (
                <>
                  <Divider
                    type="vertical"
                    style={{ backgroundColor: "#FFF" }}
                  />
                  <Tooltip title="No Customers Assigned">
                    <StarOutlined style={{ color: "red" }} />
                  </Tooltip>
                </>
              )}
              {recurring?.isRecurring && (
                <>
                  <Divider
                    type="vertical"
                    style={{ backgroundColor: "#FFF", color: "#FFF" }}
                  />
                  <Tooltip title="Recurring Project">
                    <RetweetOutlined style={{ color: "blue" }} />
                  </Tooltip>
                </>
              )}
            </>
          ) : (
            <Link>{name}</Link>
          )}
        </div>
      ),
      onCell: (record, index) => {
        return {
          onClick: () => onSelectOne(record),
        };
      },
      sorter: {
        compare: (a, b) => a.name.localeCompare(b.name),
        multiple: 1,
      },
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: "Customer(s)",
      dataIndex: "customers",
      key: "assignedTo",
      render: (_, row) => (
        <>
          {Array.isArray(row.customers) ? (
            row.customers?.slice(0, 3).map((customer) => (
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  maxWidth: 120,
                }}
              >
                {onSelectCustomer ? (
                  <Link onClick={() => onSelectCustomer(customer._id)}>
                    {customer?.DisplayName || ""}
                  </Link>
                ) : (
                  <div>{customer?.DisplayName || ""}</div>
                )}
              </div>
            ))
          ) : (
            <></>
          )}
        </>
      ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Template",
      dataIndex: "projectTemplate",
      key: "template",
      className: "table-row",
      render: (_, { projectTemplate }) => <div>{projectTemplate?.name}</div>,
      sorter: {
        compare: (a, b) =>
          (a.projectTemplate?.name || "").localeCompare(
            b.projectTemplate?.name || "",
          ),
        multiple: 1,
      },
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      className: "table-row",

      render: (_, { status }) => <div>{status?.name}</div>,
      sorter: {
        compare: (a, b) =>
          String(a.status?.name || "").localeCompare(
            String(b.status?.name || ""),
          ),
        multiple: 2,
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ["xl", "lg"],
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      className: "table-row",
      render: (_, { startDate }) => (
        <>{startDate ? new Date(startDate).toLocaleDateString() : "N/A"}</>
      ),
      sorter: {
        compare: (a, b) =>
          new Date(a.startDate || 0).getTime() -
          new Date(b.startDate || 0).getTime(),
        multiple: 3,
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ["xl", "lg"],
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      className: "table-row",
      render: (_, { endDate }) => (
        <>{endDate ? new Date(endDate).toLocaleDateString() : "N/A"}</>
      ),
      sorter: {
        compare: (a, b) =>
          new Date(a.endDate || 0).getTime() -
          new Date(b.endDate || 0).getTime(),
        multiple: 4,
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ["xl", "lg"],
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (tags: TagProps[]) => (
        <div>
          {tags.map((tag: TagProps) => (
            <Tag
              key={tag._id}
              color={tag.color}
              style={{
                maxWidth: "130px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tag.name}
            </Tag>
          ))}
        </div>
      ),
      responsive: ["xl", "lg"],
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (_, row) => (
        <div
          style={{
            maxWidth: "100px",
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {row.location?.name}
        </div>
      ),
      sorter: (a, b) =>
        (a.location?.name || "").localeCompare(String(b.location?.name || "")),
      responsive: ["xl", "lg"],
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",

      render: (_, row) => (
        <div
          style={{
            maxWidth: "100px",
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {row.source?.name}
        </div>
      ),
      sorter: (a, b) =>
        (a.source?.name || "").localeCompare(String(b.source?.name || "")),
      responsive: ["xl", "lg"],
    },
    {
      title: "Assigned",
      dataIndex: "assignedTo",
      width: "20%",
      key: "assignedTo",
      render: (_, row) => (
        <>
          {Array.isArray(row.assignedTo) ? (
            row.assignedTo?.map((assigned) => (
              <div key={assigned._id} color="blue">
                {assigned?.name}
              </div>
            ))
          ) : (
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                maxWidth: 120,
              }}
            >
              {row.assignedTo?.name}
            </div>
          )}
        </>
      ),
      responsive: ["xl", "lg", "md"],
    },
  ];
  return (
    <Table
      title={() => (
        <>
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <label>Guide</label>
            <Space direction="horizontal" size={0}>
              <Tooltip title="No Customers Assigned">
                <StarOutlined style={{ color: "red" }} />
              </Tooltip>
              <Divider type="vertical" />
              <Tooltip title="Recurring Project">
                <RetweetOutlined style={{ color: "blue" }} />
              </Tooltip>
            </Space>
          </Space>
        </>
      )}
      style={{ width: "100%", overflowX: "scroll" }}
      bordered
      loading={dataLoading}
      columns={columns}
      rowSelection={rowSelection}
      dataSource={projectListPaginated.data}
      className="summary-client__table"
      pagination={tableParams.pagination}
      size="small"
      rowKey={"_id"}
      tableLayout="auto"
      scroll={{ x: 1300 }}
    />
  );
};
